import * as THREE from 'three';
import { PORTAL_HALF_W, PORTAL_HALF_H } from '../portals/Portal.js';

/** Trace at most two segments: one portal traversal, with solid occlusion. */
export function traceBeam(world, portals, origin, direction, range, receivers = []) {
  const start = origin.clone(), dir = direction.clone().normalize();
  const segments = [], ray = new THREE.Ray(), hitPoint = new THREE.Vector3();
  const uv = new THREE.Vector2(), transform = new THREE.Matrix4();
  const hosts = new Set((portals?.portals ?? []).filter(p => p.active).map(p => p.host));
  let remaining = range;
  for (let hop = 0; hop < 2; hop++) {
    // Disabled portal hosts still occlude rays outside the aperture. Open
    // doors and broken glass do not; only active portal hosts are included.
    const wall = world.raycast(start, dir, remaining, c => c.enabled || hosts.has(c), { includeDisabled: true });
    let distance = wall?.distance ?? remaining, receiver = null, entry = null;
    ray.set(start, dir);
    for (const r of receivers) {
      if (!ray.intersectSphere(new THREE.Sphere(r.position, r.radius), hitPoint)) continue;
      const d = start.distanceTo(hitPoint);
      if (d < distance) { distance = d; receiver = r; }
    }
    if (hop === 0 && portals?.isLinked('player')) {
      const pair = portals.pair('player');
      for (const p of [pair.a, pair.b]) {
        const approach = dir.dot(p.normal);
        if (approach >= -1e-6) continue;
        const d = -p.signedDistance(start) / approach;
        if (d <= 0.001 || d > distance + 0.025) continue;
        hitPoint.copy(start).addScaledVector(dir, d);
        p.localUV(hitPoint, uv);
        if ((uv.x / PORTAL_HALF_W) ** 2 + (uv.y / PORTAL_HALF_H) ** 2 > 1) continue;
        distance = d; entry = p; receiver = null;
      }
    }
    const end = start.clone().addScaledVector(dir, distance);
    segments.push({ start: start.clone(), end });
    if (receiver) return { segments, receiver };
    if (!entry) break;
    remaining -= distance;
    if (remaining <= 0.02) break;
    portals.getTransform(entry, entry.link, transform);
    start.copy(end).applyMatrix4(transform).addScaledVector(entry.link.normal, 0.03);
    dir.transformDirection(transform);
  }
  return { segments, receiver: null };
}

/** Level-owned state: sources feed sockets, then gates consume the result. */
export class BeamNetwork {
  constructor(entities, world) {
    this.world = world;
    this.emitters = entities.filter(e => e.role === 'emitter');
    this.receivers = entities.filter(e => e.role === 'receptacle');
    this.gates = entities.filter(e => e.role === 'gate');
  }

  update(dt, ctx) {
    for (const r of this.receivers) r.powered = false;
    for (const e of this.emitters) {
      const result = traceBeam(this.world, ctx.portals, e.position, e.direction, e.range, this.receivers);
      e.showSegments(result.segments);
      if (result.receiver) result.receiver.powered = true;
    }
    for (const r of this.receivers) r.showPower();
    for (const gate of this.gates) {
      const powered = this.receivers.some(r => r.id === gate.link && r.powered);
      gate.setPower(powered, dt, ctx);
    }
  }
}
